// FILE: components/paths/ReviewReaderCard.tsx
"use client";

import { useState } from "react";
import { BookOpen, Star, CheckCircle2, ExternalLink, ChevronDown } from "lucide-react";

interface ReviewReaderCardProps {
  index: number;
  review: {
    id: string;
    title: string;
    emoji?: string;
    content: string;
    author?: string;
    rating?: number;
    stars?: number;
  };
  isRead?: boolean;
  onMarkRead?: () => void;
  variant?: "embedded" | "full";
}

export default function ReviewReaderCard({
  index, review, isRead, onMarkRead, variant = "embedded",
}: ReviewReaderCardProps) {
  const [expanded, setExpanded] = useState(false);
  const starCount = review.stars ?? 0;

  // Lấy vài dòng đầu (plain text, max 120 chars)
  const previewText = review.content
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 150);

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${
      expanded
        ? "border-amber-300 shadow-[0_2px_12px_rgba(245,158,11,.12)]"
        : "border-[#e2e8f0] hover:border-amber-200"
    }`}>
      {/* ── Header (luôn hiện) ── */}
      <button
        type="button"
        onClick={() => setExpanded(p => !p)}
        className="w-full text-left flex items-start gap-3 px-5 pt-4 pb-3 bg-gradient-to-br from-amber-50/60 to-white"
      >
        <div className={`w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-extrabold shrink-0 mt-0.5 ${
          isRead ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-700"
        }`}>
          {String(index + 1).padStart(2, "0")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <BookOpen className="w-3 h-3 text-amber-600" />
            <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">
              Bài đọc tham khảo
            </span>
            {isRead && (
              <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200 ml-1">
                ✓ Đã đọc
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {review.emoji && <span className="text-lg leading-none">{review.emoji}</span>}
            <h3 className="text-[14px] font-bold text-teal-900 leading-snug line-clamp-2">{review.title}</h3>
          </div>
          {(review.author || starCount > 0) && (
            <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#94a3b8]">
              {review.author && <span>✍️ {review.author}</span>}
              {starCount > 0 && (
                <span className="flex items-center gap-0.5">
                  {Array.from({ length: starCount }).map((_, i) => (
                    <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                  ))}
                </span>
              )}
            </div>
          )}
          {/* Preview text khi thu gọn */}
          {!expanded && previewText && (
            <p className="text-[12px] text-[#64748b] leading-relaxed mt-2 line-clamp-2">
              {previewText}...
            </p>
          )}
        </div>
      </button>

      {/* ── Expand indicator bar ── */}
      <button
        type="button"
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center justify-center py-1.5 border-t border-[#f1f5f9] hover:bg-amber-50/50 transition-colors"
      >
        <ChevronDown className={`w-4 h-4 text-[#94a3b8] transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
      </button>

      {/* ── Collapsible content ── */}
      <div
  className="grid transition-all duration-300 ease-in-out"
  style={{
    gridTemplateRows: expanded ? "1fr" : "0fr",
    opacity: expanded ? 1 : 0,
  }}
>
  <div className="overflow-hidden min-h-0">
          {/* Content HTML (TipTap output) */}
          <div
            className="px-5 py-4 prose prose-sm max-w-none
              prose-headings:text-teal-900 prose-headings:font-bold
              prose-p:text-[13px] prose-p:leading-[1.75] prose-p:text-[#334155]
              prose-img:rounded-xl prose-img:my-3
              prose-a:text-teal-700 prose-a:no-underline hover:prose-a:underline
              prose-iframe:rounded-xl prose-iframe:w-full prose-iframe:aspect-video
              prose-video:rounded-xl prose-video:w-full
              prose-strong:text-[#0f172a]
              prose-ul:text-[13px] prose-ol:text-[13px]"
            dangerouslySetInnerHTML={{ __html: review.content || "" }}
          />

          {/* Footer */}
          {variant === "embedded" && (
            <div className="px-5 py-3 border-t border-[#e2e8f0] bg-[#f8fafc] flex items-center justify-between gap-3">
              <a
                href={`/reviews/${review.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] text-[#94a3b8] hover:text-teal-700 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Mở bài đầy đủ
              </a>
              <button
                onClick={(e) => { e.stopPropagation(); onMarkRead?.(); }}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-[12px] font-bold transition-all ${
                  isRead
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-teal-700 text-white hover:bg-teal-600 shadow-[0_2px_8px_rgba(15,58,74,.2)]"
                }`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {isRead ? "Đã đọc" : "Đã đọc, tiếp tục"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
